/**
 * 符卡练习选择：关卡 → 中/大 boss → 符卡 → 难度 → Z 进入选人（满火力 1 命）。
 * ↑↓ 换行，←→ 换值，Z 确认（在"开始"行），X 返回标题。
 * 下半屏为所选符卡的介绍面板：符名 + 归属 boss + 该符的自述介绍文
 * （phase.desc，boss 口吻；多语经 L() 走 CONTENT 表）。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { cycleIndex, menuNav, valueDir } from '../engine/menu.js';
import { DIFFICULTIES, EXTRA_DIFF } from '../content/difficulties.js';
import { readSpellHistory } from '../game/spellHistory.js';
import { t, diffName, L } from '../i18n.js';
import { drawFittedText } from '../engine/wrapText.js';
import * as BF from '../content/stages/bosses.js';

const STAGE_NAMES = ['云端·天空', '深海·水压', '齿轮·机械', '腐叶·森林', '幽影·暗界', '星界·终焉', '终焉回廊'];
const KINDS = ['mid', 'big'];

/** 介绍面板布局（tools/ui-fit-check.mjs 据此做溢出回归断言，改动需同步过检） */
export const DESC_PANEL = { x: 36, y: 386, maxWidth: VIEW_W - 72, bottomLimit: VIEW_H - 30 };

function bossDef(stageIndex, kind) {
  // EXTRA 可选道中（彗星骑士返场 = s6Mid）与大 boss（星灵）
  if (stageIndex === 6) return kind === 'mid' ? BF.s6Mid() : BF.exBig();
  return BF[`s${stageIndex + 1}${kind === 'mid' ? 'Mid' : 'Big'}`]();
}

export const SpellPracticeScene = {
  mgr: null,

  enter() {
    // 状态记忆：仅"从练习返回"时恢复上次练习的那张卡（一次性标记；
    // 从标题等其他入口进入一律默认——S1 道中 boss 起始）
    const shared = this.mgr.shared || {};
    const last = shared.practiceReturn ? shared.practiceLast || null : null;
    shared.practiceReturn = false; // 一次性消费
    this.stageIndex = last ? last.stageIndex : 0;
    this.kind = last ? last.kind : 'mid'; // 默认道中 boss（进阶顺序：道中→关底）
    this.phaseIndex = last ? last.phaseIndex : 0;
    this.diffIndex = last ? last.diffIndex : 1;
    this.row = 0; // 0 关卡 1 boss 2 符卡 3 难度 4 开始
  },

  exit() {},

  /** 当前 boss 的符卡阶段列表 */
  _spells() {
    return bossDef(this.stageIndex, this.kind).phases.filter((p) => p.spellCard);
  },

  /** 本地符卡历史（{attempts, captured}，gameScene._practiceEnd 记录；共享实现，勿再私写一份）。 */
  _hist() {
    return readSpellHistory();
  },

  update() {
    const audio = this.mgr.shared.audio;
    const spells = this._spells();
    if (this.phaseIndex >= spells.length) this.phaseIndex = Math.max(0, spells.length - 1); // 空列表不得夹出 -1

    if (Input.pressed('cancel')) {
      audio.playSfx('select');
      this.mgr.switchTo('title');
      return;
    }
    const nav = menuNav(Input, audio, this.row, 5);
    if (nav !== null) this.row = nav;
    const dir = valueDir(Input, audio);
    if (dir !== 0) {
      if (this.row === 0) {
        this.stageIndex = cycleIndex(this.stageIndex, 7, dir); // 1-6 关 + EXTRA
        this.phaseIndex = 0;
        if (this.stageIndex === 6) this.kind = 'big'; // 进 EXTRA 默认关底（道中可在下一行切换）
      } else if (this.row === 1) {
        this.kind = KINDS[cycleIndex(KINDS.indexOf(this.kind), 2, dir)]; // EXTRA 也可选道中/大 boss
        this.phaseIndex = 0;
      } else if (this.row === 2) {
        this.phaseIndex = cycleIndex(this.phaseIndex, spells.length, dir);
      } else if (this.row === 3) {
        if (this.stageIndex !== 6) { // EXTRA 难度固定，不可选
          this.diffIndex = cycleIndex(this.diffIndex, DIFFICULTIES.length, dir);
        }
      }
    }
    if (Input.pressed('confirm') && this.row === 4) {
      audio.playSfx('select');
      this.mgr.shared.practiceLast = { // 记忆本次选择，练习结束返回时恢复
        stageIndex: this.stageIndex, kind: this.kind, phaseIndex: this.phaseIndex, diffIndex: this.diffIndex,
      };
      this.mgr.switchTo('charSelect', {
        mode: 'practice',
        difficulty: this.stageIndex === 6 ? EXTRA_DIFF : DIFFICULTIES[this.diffIndex],
        practice: {
          stageIndex: this.stageIndex,
          kind: this.kind,
          phaseIndex: this.phaseIndex,
        },
      });
    }
  },

  draw(r) {
    r.text(t('practice.title'), VIEW_W / 2, 28, { size: 22, color: '#fff', align: 'center' });
    r.text(t('practice.hint'), VIEW_W / 2, 58, { size: 11, color: '#889', align: 'center' });

    const spells = this._spells();
    const boss = bossDef(this.stageIndex, this.kind);
    const stageLabel = this.stageIndex === 6
      ? `${diffName(EXTRA_DIFF)} · ${L(STAGE_NAMES[6])}`
      : `STAGE ${this.stageIndex + 1} · ${L(STAGE_NAMES[this.stageIndex])}`;
    // 符卡历史（取得/挑战，东方符卡列表惯例）
    const spellIdx = Math.min(this.phaseIndex, Math.max(0, spells.length - 1));
    const rec = this._hist()[`${this.stageIndex}-${this.kind}-${spellIdx}`];
    const histText = rec ? `${rec.captured}/${rec.attempts}` : '0/0';
    const histColor = rec && rec.captured > 0 ? '#f4c' : '#889';
    const rows = [
      [t('practice.stage'), stageLabel],
      [t('practice.boss'), `${this.kind === 'mid' ? t('practice.mid') : t('practice.big')} · ${L(boss.name)}`],
      [t('practice.spell'), spells.length ? L(spells[spellIdx].name) : '-'],
      [t('practice.diff'), this.stageIndex === 6 ? diffName(EXTRA_DIFF) : diffName(DIFFICULTIES[this.diffIndex])],
      ['', t('practice.start')],
    ];
    for (let i = 0; i < rows.length; i++) {
      const y = 96 + i * 44;
      const sel = i === this.row;
      if (sel) r.text('▶', 70, y + 2, { size: 15, color: '#ff4' });
      if (rows[i][0]) r.text(rows[i][0], 100, y, { size: 13, color: sel ? '#8cf' : '#778' });
      r.text(rows[i][1], 210, y, { size: 15, color: sel ? '#ff4' : '#ccd' });
      // 符卡行追加历史计数（取得/挑战，置于符名下一行防重叠）
      if (i === 2 && spells.length) {
        r.text(`[${histText}]`, 210, y + 24, { size: 11, color: histColor });
      }
    }

    // ---- 符卡介绍面板（所选符的自述介绍文，boss 口吻） ----
    r.rect(30, 330, VIEW_W - 60, 1, { fill: '#446' });
    if (spells.length) {
      const sp = spells[spellIdx];
      r.text(L(sp.name), 36, 342, { size: 15, color: '#ff4' });
      const attribution = `—— ${L(boss.name)}${boss.title ? ' · ' + L(boss.title) : ''}`;
      r.text(attribution, 36, 364, { size: 11, color: '#8cf' });
      if (sp.desc) drawFittedText(r, L(sp.desc), DESC_PANEL.x, DESC_PANEL.y, DESC_PANEL.maxWidth, DESC_PANEL.bottomLimit);
    }

    r.text(t('practice.note'), VIEW_W / 2, VIEW_H - 22, { size: 10, color: '#889', align: 'center' });
  },
};
